import type { EntityConfigDefinition, DisplayColumnConfig, FilterConfig } from '../../types/entityConfig'
import { TABLE_PRODUCT, TABLE_SERVICE_PARAM } from '../../utils/displayFormatters'

const config: EntityConfigDefinition = {
  name: 'SERVICE_PARAM_MAPPING',
  idField: 'SERVICE_PARAM_MAPPING_ID',
  
  
  // Use optimized query for filtering
  useOptimizedQuery: true,
  optimizedQueryName: 'getFilteredServiceParamMappings',
  supportsPagination: true,
  
  // Form processing configuration
  keepAuditFieldsOnUpdate: false,
  
  // Environment copy configuration
  preserveOnCopy: ['SERVICE_PARAM_MAPPING_ID'],
  
  // Data loading configuration
  loadProductOptions: true,
  loadServiceOptions: true,
  
  // Filter configuration
  hasFilters: true,
  requiresProductFilter: true,
  showEmptyUntilFiltered: true,
  requiresTargetServiceFilter: false,
  
  // Foreign key configuration for table display
  foreignKeys: {
    ORIGIN_PRODUCT_ID: {
      table: 'ORIGIN_PRODUCT',
      valueField: 'ORIGIN_PRODUCT_ID',
      displayField: 'PRODUCT_ID',
      displayFormat: TABLE_PRODUCT
    },
    SOURCE_SERVICE_PARAM_ID: {
      table: 'SERVICE_PARAM',
      valueField: 'SERVICE_PARAM_ID',
      displayField: 'PARAM_NAME',
      displayFormat: TABLE_SERVICE_PARAM
    },
    TARGET_SERVICE_PARAM_ID: {
      table: 'SERVICE_PARAM',
      valueField: 'SERVICE_PARAM_ID',
      displayField: 'PARAM_NAME',
      displayFormat: TABLE_SERVICE_PARAM
    }
  },
  
  // Fields to remove before GraphQL mutations
  fieldsToRemove: [
    'ORIGIN_PRODUCT_ID_DISPLAY',
    'SOURCE_SERVICE_DISPLAY',
    'SOURCE_SERVICE_PARAM_ID_DISPLAY', 
    'TARGET_SERVICE_DISPLAY',
    'TARGET_SERVICE_PARAM_ID_DISPLAY',
    'CREATED_BY_USER_ID',
    'CREATED_DATE'
  ],
  

  
  // Auto-create missing foreign key records
  autoCreateForeignKeys: {
    ORIGIN_PRODUCT_ID: {
      entity: 'ORIGIN_PRODUCT',
      matchFields: ['PRODUCT_ID'],
      copyFields: ['PRODUCT_ID', 'VENDOR_NAME', 'PRODUCT_DESC', 'PSCU_CLIENT_ID', 'PARTNER_CODE']
    },
    SOURCE_SERVICE_PARAM_ID: {
      entity: 'SERVICE_PARAM',
      matchFields: ['PARAM_NAME'],
      copyFields: ['SERVICE_ID', 'PARAM_NAME']
    },
    TARGET_SERVICE_PARAM_ID: {
      entity: 'SERVICE_PARAM',
      matchFields: ['PARAM_NAME'],
      copyFields: ['SERVICE_ID', 'PARAM_NAME']
    }
  },
  
  // Integrated service selection for parameter filtering
  integratedFilters: {
    SOURCE_SERVICE_PARAM_ID: {
      filterField: 'SOURCE_SERVICE_ID',
      filterLabel: 'Source Service',
      filterSource: 'SERVICE',
      filterValueField: 'SERVICE_ID',
      filterDisplayField: 'URI'
    },
    TARGET_SERVICE_PARAM_ID: {
      filterField: 'TARGET_SERVICE_ID', 
      filterLabel: 'Target Service',
      filterSource: 'SERVICE',
      filterValueField: 'SERVICE_ID',
      filterDisplayField: 'URI'
    }
  },
  
  // Filter configuration for product selection
  filterType: 'product',
  
  // Custom filters for this entity
  customFilters: {
    pscuClient: {
      label: 'Filter by PSCU Client ID',
      field: 'PSCU_CLIENT_ID',
      type: 'pscuClient',
      required: false,
      showAlways: true
    },
    sourceService: {
      label: 'Filter by Source Service',
      field: 'SOURCE_SERVICE_PARAM_ID',
      type: 'service',
      required: false,
      showAlways: true
    },
    targetService: {
      label: 'Filter by Target Service',
      field: 'TARGET_SERVICE_PARAM_ID',
      type: 'service',
      required: false,
      showAlways: true
    }
  },
  
  // Modal pre-population configuration
  modalPrePopulation: {
    productFilter: 'ORIGIN_PRODUCT_ID',
    sourceServiceFilter: 'SOURCE_SERVICE_ID',
    targetServiceFilter: 'TARGET_SERVICE_ID'
  },
  
  // Data loading configuration
  requiresServiceParamMappings: true,
  
  // UI configuration
  dateFieldPattern: 'DATE',
  filterChangeHandler: 'filterServiceParamMappings',
  buttonText: {
    create: 'Add New',
    viewOnly: 'View Only Mode'
  },
  emptyStateMessages: {
    requiresProductFilter: 'Please select a product from the filter above to view SERVICE_PARAM_MAPPING records.',
    default: 'No SERVICE_PARAM_MAPPING records found.'
  },
  
  // Environment comparison configuration
  comparisonConfig: {
    matchingFields: ['ORIGIN_PRODUCT_ID', 'SOURCE_SERVICE_PARAM_ID', 'TARGET_SERVICE_PARAM_ID'],
    comparisonFields: ['ORIGIN_PRODUCT_ID', 'SOURCE_SERVICE_PARAM_ID', 'TARGET_SERVICE_PARAM_ID', 'SYSTEM_NBR', 'PRIN_NBR', 'AGENT_NBR', 'PLASTIC_TYPE_ID', 'COMMENT_TEXT'],
    useDisplayValues: {
      'ORIGIN_PRODUCT_ID': 'PRODUCT_ID',
      'SOURCE_SERVICE_PARAM_ID': 'PARAM_NAME',
      'TARGET_SERVICE_PARAM_ID': 'PARAM_NAME'
    },
    useEnhancedValues: true,
    requiresProductFilter: true,
    showEmptyUntilFiltered: true,
    productFilterField: 'ORIGIN_PRODUCT_ID',
    productFilterMapping: {
      sourceField: 'PRODUCT_ID',
      targetField: 'ORIGIN_PRODUCT_ID'
    },
    displayFieldMapping: {
      ORIGIN_PRODUCT_ID: 'PRODUCT_ID',
      SOURCE_SERVICE_PARAM_ID: 'PARAM_NAME',
      TARGET_SERVICE_PARAM_ID: 'PARAM_NAME'
    },
    displayFields: ['ORIGIN_PRODUCT_ID', 'SYSTEM_NBR', 'PRIN_NBR', 'AGENT_NBR', 'SOURCE_SERVICE_PARAM_ID', 'TARGET_SERVICE_PARAM_ID', 'PLASTIC_TYPE_ID', 'COMMENT_TEXT'],
    resolveLookupsForBothEnvironments: true
  },
  
  // Display columns with foreign key lookups
  displayColumns: [
    {
      field: 'ORIGIN_PRODUCT_ID',
      lookup: {
        table: 'ORIGIN_PRODUCT',
        foreignKey: 'ORIGIN_PRODUCT_ID',
        displayField: 'PRODUCT_ID'
      }
    },
    {
      field: 'SOURCE_SERVICE_PARAM_ID',
      lookup: {
        table: 'SERVICE_PARAM',
        foreignKey: 'SERVICE_PARAM_ID',
        displayField: 'PARAM_NAME'
      }
    },
    {
      field: 'TARGET_SERVICE_PARAM_ID',
      lookup: {
        table: 'SERVICE_PARAM',
        foreignKey: 'SERVICE_PARAM_ID',
        displayField: 'PARAM_NAME'
      }
    }
  ] as DisplayColumnConfig[],

  // Unified filter configuration
  filters: {
    table: [
      { type: 'custom', field: 'PSCU_CLIENT_ID', label: 'Filter by PSCU Client ID' },
      { type: 'product', field: 'ORIGIN_PRODUCT_ID', label: 'Filter by Product', required: true },
      { type: 'service', field: 'SOURCE_SERVICE_PARAM_ID', label: 'Filter by Source Service' },
      { type: 'service', field: 'TARGET_SERVICE_PARAM_ID', label: 'Filter by Target Service' }
    ] as FilterConfig[],
    modal: [
      { type: 'integrated', field: 'ORIGIN_PRODUCT_ID', label: 'Product', source: 'ORIGIN_PRODUCT' },
      { type: 'integrated', field: 'SOURCE_SERVICE_ID', label: 'Source Service', source: 'SERVICE' },
      { type: 'integrated', field: 'TARGET_SERVICE_ID', label: 'Target Service', source: 'SERVICE' }
    ] as FilterConfig[],
    requiresProduct: true,
    showEmptyUntilFiltered: true
  },

  // Fields configuration
  fields: [
    "SERVICE_PARAM_MAPPING_ID",
    "ORIGIN_PRODUCT_ID",
    "SYSTEM_NBR",
    "PRIN_NBR",
    "AGENT_NBR",
    "SOURCE_SERVICE_PARAM_ID",
    "TARGET_SERVICE_PARAM_ID",
    "PLASTIC_TYPE_ID",
    "COMMENT_TEXT",
    "CREATED_BY_USER_ID",
    "CREATED_DATE",
    "CHANGED_BY_USER_ID",
    "CHANGED_DATE"
  ],
  
  // Form fields configuration
  formFields: [
    {
      "name": "ORIGIN_PRODUCT_ID",
      "type": "select",
      "required": true,
      "disabled": false
    },
    {
      "name": "SYSTEM_NBR",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "PRIN_NBR",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "AGENT_NBR",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "SOURCE_SERVICE_PARAM_ID",
      "type": "select",
      "required": true,
      "disabled": false
    },
    {
      "name": "TARGET_SERVICE_PARAM_ID",
      "type": "select",
      "required": true,
      "disabled": false
    },
    {
      "name": "PLASTIC_TYPE_ID",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "COMMENT_TEXT",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "CREATED_BY_USER_ID",
      "type": "number",
      "required": false,
      "disabled": false
    },
    {
      "name": "CREATED_DATE",
      "type": "text",
      "required": false,
      "disabled": true
    },
    {
      "name": "CHANGED_BY_USER_ID",
      "type": "number",
      "required": false,
      "disabled": false
    },
    {
      "name": "CHANGED_DATE",
      "type": "text",
      "required": false,
      "disabled": true
    }
  ]
}

export default config

